import React, { useState } from "react";
import { Modal, Row, Col, Button, Image, Empty } from "antd";
import { EyeOutlined, FilePdfOutlined, PlusOutlined } from "@ant-design/icons";
import { GET_LOAD_IMAGE_NEW_BY } from "../../../API/apiUrls";
import { MENU_TYPES } from "../constants";
import { getDocLabel, getFileType } from "../utils";
import PdfPreviewModal from "../components/PdfPreviewModal";

// ── สีประจำโซนแต่ละ menuType (วนซ้ำถ้ามี menuType มากกว่าจำนวนสี) ──
const ZONE_PALETTE = [
  { color: "#d46b08", bg: "#fff7e6", border: "#ffd591" },
  { color: "#096dd9", bg: "#e6f4ff", border: "#91caff" },
  { color: "#389e0d", bg: "#f6ffed", border: "#b7eb8f" },
  { color: "#722ed1", bg: "#f9f0ff", border: "#d3adf7" },
  { color: "#c41d7f", bg: "#fff0f6", border: "#ffadd2" },
];

const UploadImageModal = ({
  open,
  onClose,
  fileListByContractCourt = [],
  token,
  onAddImage,
}) => {
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState("");

  // ── sort: menuType ASC → imageType ASC ───────────────────────
  const sorted = [...fileListByContractCourt].sort((a, b) =>
    a.menuType !== b.menuType
      ? a.menuType - b.menuType
      : a.imageType - b.imageType,
  );

  // ── แบ่ง Zone ตาม menuType ที่พบจริงในข้อมูล ──────────────────
  const menuTypesPresent = [...new Set(sorted.map((f) => f.menuType))].sort(
    (a, b) => a - b,
  );

  // ── card รูปภาพ ───────────────────────────────────────────────
  const renderImageCard = (file, zone) => {
    const previewUrl = `${GET_LOAD_IMAGE_NEW_BY}/${file.path}?token=${token}`;
    const label = getDocLabel(file.imageType);

    return (
      <Col key={file.imageId} xs={8} sm={6} md={4}>
        <div
          style={{
            border: `1px solid ${zone.color}`,
            borderRadius: 8,
            padding: 8,
            textAlign: "center",
            background: "#fff",
          }}
        >
          <Image
            src={previewUrl}
            alt={file.path}
            style={{
              width: "100%",
              height: 80,
              objectFit: "cover",
              borderRadius: 4,
            }}
            preview={{
              mask: (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <EyeOutlined style={{ fontSize: 14 }} />
                  <span style={{ fontSize: 10 }}>ดูรูป</span>
                </div>
              ),
            }}
          />
          <br />
          <div
            style={{
              marginTop: 6,
              background: zone.color,
              color: "#fff",
              fontSize: 11,
              borderRadius: 4,
              padding: "1px 6px",
              display: "inline-block",
            }}
          >
            {label}
          </div>
        </div>
      </Col>
    );
  };

  // ── card PDF ──────────────────────────────────────────────────
  const renderPdfCard = (file, zone) => {
    const previewUrl = `${GET_LOAD_IMAGE_NEW_BY}/${file.path}?token=${token}`;
    const label = getDocLabel(file.imageType);
    return (
      <Col key={file.imageId} xs={8} sm={6} md={4}>
        <div
          style={{
            border: `1px solid ${zone.color}`,
            borderRadius: 8,
            padding: 8,
            textAlign: "center",
            background: "#fff",
          }}
        >
          <div
            onClick={() => {
              setPdfPreviewUrl(previewUrl);
              setPdfPreviewOpen(true);
            }}
            style={{
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: 80,
              background: "#fff1f0",
              borderRadius: 6,
              border: "1px dashed #ff4d4f",
              gap: 4,
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            <FilePdfOutlined style={{ fontSize: 32, color: "#ff4d4f" }} />
            <span style={{ fontSize: 11, color: "#ff4d4f", fontWeight: 500 }}>
              <EyeOutlined style={{ marginRight: 4 }} />
              ดู PDF
            </span>
          </div>
          <div
            style={{
              marginTop: 6,
              background: zone.color,
              color: "#fff",
              fontSize: 11,
              borderRadius: 4,
              padding: "1px 6px",
              display: "inline-block",
            }}
          >
            {label}
          </div>
        </div>
      </Col>
    );
  };

  // ── render Zone ───────────────────────────────────────────────
  const renderZone = (menuType, index) => {
    const files = sorted.filter((f) => f.menuType === menuType);
    if (!files.length) return null;
    const zone = ZONE_PALETTE[index % ZONE_PALETTE.length];
    const title =
      MENU_TYPES.find((m) => m.id === menuType)?.label ??
      `เมนู ${menuType}`;
    const pdfFiles = files.filter((f) => getFileType(f.path) === "pdf");
    const imgFiles = files.filter((f) => getFileType(f.path) === "image");

    return (
      <div
        key={menuType}
        style={{
          border: `1px solid ${zone.border}`,
          borderRadius: 10,
          padding: "12px 16px",
          marginBottom: 16,
          background: zone.bg,
        }}
      >
        {/* Zone Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
            paddingBottom: 8,
            borderBottom: `1px solid ${zone.border}`,
          }}
        >
          <div
            style={{
              background: zone.color,
              color: "#fff",
              borderRadius: 6,
              padding: "2px 12px",
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {title}
          </div>
          <span style={{ fontSize: 14, color: "#888" }}>
            {files.length} ไฟล์
          </span>
        </div>

        {/* รูปภาพ */}
        {imgFiles.length > 0 && (
          <>
            <div style={{ fontSize: 14, color: "#888", marginBottom: 6 }}>
              รูปภาพ ({imgFiles.length})
            </div>
            <Image.PreviewGroup>
              <Row
                gutter={[8, 8]}
                style={{ marginBottom: pdfFiles.length ? 12 : 0 }}
              >
                {imgFiles.map((f) => renderImageCard(f, zone))}
              </Row>
            </Image.PreviewGroup>
          </>
        )}

        {/* PDF */}
        {pdfFiles.length > 0 && (
          <>
            <div
              style={{
                fontSize: 14,
                color: "#888",
                marginBottom: 6,
                marginTop: imgFiles.length ? 4 : 0,
              }}
            >
              <FilePdfOutlined style={{ color: "#ff4d4f", marginRight: 4 }} />
              เอกสาร PDF ({pdfFiles.length})
            </div>
            <Row gutter={[8, 8]}>{pdfFiles.map((f) => renderPdfCard(f, zone))}</Row>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        centered
        width={880}
        title="เอกสารแนบ"
      >
        {onAddImage && (
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={onAddImage}
            style={{ marginBottom: 16 }}
          >
            อัพโหลด รูป/ไฟล์
          </Button>
        )}

        {menuTypesPresent.length > 0 ? (
          menuTypesPresent.map((menuType, index) => renderZone(menuType, index))
        ) : (
          <Empty description="ไม่พบเอกสาร" />
        )}
      </Modal>

      {/* PDF Preview Modal */}
      <PdfPreviewModal
        open={pdfPreviewOpen}
        url={pdfPreviewUrl}
        onClose={() => {
          setPdfPreviewOpen(false);
          setPdfPreviewUrl("");
        }}
      />
    </>
  );
};

export default UploadImageModal;
